import type { PhotoVariant } from "@/lib/photos";

export type { PhotoVariant };

function toSrcSet(items: { url: string; width: number }[]) {
  return items.map((item) => `${item.url} ${item.width}w`).join(", ");
}

export function Photo({
  src,
  avif,
  variants,
  alt,
  width,
  height,
  sizes,
  eager = false,
  priority = false,
  className = "",
}: {
  src: string;
  avif?: string;
  variants?: readonly PhotoVariant[];
  alt: string;
  width: number;
  height: number;
  sizes?: string;
  /** Eager-load without fetchpriority so a matching AVIF preload can win LCP. */
  eager?: boolean;
  /** Eager-load and mark as high priority (also preloads the WebP fallback). */
  priority?: boolean;
  className?: string;
}) {
  const loadEager = priority || eager;
  const webpSrcSet = variants
    ? toSrcSet(variants.map((variant) => ({ url: variant.src, width: variant.width })))
    : src.includes(".webp")
      ? src
      : undefined;
  const avifSrcSet = variants
    ? toSrcSet(
        variants.flatMap((variant) =>
          variant.avif ? [{ url: variant.avif, width: variant.width }] : [],
        ),
      )
    : avif;

  return (
    <picture className="contents">
      {avifSrcSet ? <source type="image/avif" srcSet={avifSrcSet} sizes={sizes} /> : null}
      {webpSrcSet ? <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} /> : null}
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        srcSet={webpSrcSet}
        sizes={sizes}
        loading={loadEager ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : undefined}
        decoding="async"
        className={className}
      />
    </picture>
  );
}
